import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Contact page — mirrors the floating <ContactUs/> form but as a full
 * page reachable from the footer.  Uses the same POST /api/contact
 * endpoint (Resend delivery) so admins get the message in the shared inbox.
 */
export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) {
      setErr("Please include your email and a message.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send right now — try again in a moment.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="contact-hero"
        eyebrow="Get in touch"
        title="Contact Wikificial"
        sub="Missing a tool? Spotted a bad score? Have a partnership idea? Send us a note — we read every one."
      />

      {sent ? (
        <div className="rounded-2xl bg-white border border-[#E7EAF0] p-8 text-center" data-testid="contact-sent">
          <CheckCircle2 size={36} className="mx-auto mb-3 text-[#F97316]"/>
          <h2 className="font-display text-lg font-bold text-[#0B1220] mb-1 tracking-tight">Message received.</h2>
          <p className="text-[13px] text-[#5B6478]">We'll get back to {form.email} within two business days.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl bg-white border border-[#E7EAF0] p-6 sm:p-8 grid gap-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Name</label>
              <input value={form.name} onChange={set("name")} placeholder="Your name" className="w-full h-10 rounded-md bg-white border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40" data-testid="contact-name"/>
            </div>
            <div>
              <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Email *</label>
              <input type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="w-full h-10 rounded-md bg-white border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40" data-testid="contact-email"/>
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Subject</label>
            <input value={form.subject} onChange={set("subject")} placeholder="What's this about?" className="w-full h-10 rounded-md bg-white border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40" data-testid="contact-subject"/>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Message *</label>
            <textarea required rows={6} value={form.message} onChange={set("message")} placeholder="Tell us what's on your mind…" className="w-full rounded-md bg-white border border-[#E7EAF0] px-3 py-2.5 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40 resize-none" data-testid="contact-message"/>
          </div>
          {err && <div className="text-[12.5px] text-rose-600" data-testid="contact-error">{err}</div>}
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-[11.5px] text-[#8A93A6] flex items-center gap-1.5">
              <Mail size={11}/> contact@wikifi.app
            </div>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-5 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold flex items-center gap-1.5 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="contact-submit"
            >
              {busy ? <><Loader2 size={13} className="animate-spin"/> Sending…</> : <><Send size={12}/> Send message</>}
            </button>
          </div>
        </form>
      )}
    </Layout>
  );
}
